import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { Controller, FieldValues, UseFormReturn } from 'react-hook-form';

import { useGetProjectsQuery } from 'features/projects';
import { Check, ChevronsUpDown, Copy, RefreshCw, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

import { generatePassword } from '../utils/password';
import { UsersInput as Input } from './ui/UsersInput';

export type UserInfoVariant = 'full' | 'userConfig' | 'projectPermissions';

interface Props<T extends FieldValues> {
  form: UseFormReturn<T>;
  mode: 'create' | 'edit';
  variant?: UserInfoVariant;
  canToggleActive?: boolean;
  isPendingActivation?: boolean;
  isSelf?: boolean;
  isAdminUser?: boolean;
}

const COPIED_FEEDBACK_MS = 2000;

// `as never` casts bridge RHF's generic name-paths to the concrete shape we use.
// The dialog owns the schema; sections only read/write known field names.
const fieldName = (name: string): never => name as never;

const matchesQuery = (name: string, id: number, query: string): boolean =>
  name.toLowerCase().includes(query) || String(id).includes(query);

const UserInfoSection = <T extends FieldValues>({
  form,
  mode,
  variant = 'full',
  canToggleActive = false,
  isPendingActivation = false,
  isSelf = false,
  isAdminUser = false,
}: Props<T>) => {
  const { data: projects = [] } = useGetProjectsQuery();
  const masterProjects = projects.filter((p) => p.masterProjectId === null);

  const [showCopied, setShowCopied] = useState(false);
  const [projectPopoverOpen, setProjectPopoverOpen] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [subProjectSearch, setSubProjectSearch] = useState('');
  const [masterProjectSearch, setMasterProjectSearch] = useState('');

  const projectId = form.watch(fieldName('projectId')) as unknown as number | null;
  const projectIds = (form.watch(fieldName('projectIds')) as unknown as number[]) ?? [];
  const subProjectIds = (form.watch(fieldName('subProjectIds')) as unknown as number[]) ?? [];

  const selectedMaster = masterProjects.find((p) => p.id === projectId) ?? null;
  const subProjectOptions = [...(selectedMaster?.subProjects ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
  );

  const sortedMasterProjects = useMemo(
    () => [...masterProjects].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
    [masterProjects]
  );
  const filteredMasterProjects = useMemo(() => {
    const query = projectSearch.trim().toLowerCase();
    const base = query ? sortedMasterProjects.filter((p) => matchesQuery(p.name, p.id, query)) : sortedMasterProjects;
    const selected = base.filter((p) => p.id === projectId);
    const rest = base.filter((p) => p.id !== projectId);
    return [...selected, ...rest];
  }, [sortedMasterProjects, projectSearch, projectId]);
  const selectedProjectName = masterProjects.find((p) => p.id === projectId)?.name ?? null;

  const filteredMasterProjectsForChecklist = useMemo(() => {
    const query = masterProjectSearch.trim().toLowerCase();
    const base = query ? sortedMasterProjects.filter((p) => matchesQuery(p.name, p.id, query)) : sortedMasterProjects;
    const selected = base.filter((p) => projectIds.includes(p.id));
    const rest = base.filter((p) => !projectIds.includes(p.id));
    return [...selected, ...rest];
  }, [sortedMasterProjects, masterProjectSearch, projectIds]);

  const filteredSubProjectOptions = useMemo(() => {
    const query = subProjectSearch.trim().toLowerCase();
    return query ? subProjectOptions.filter((sp) => matchesQuery(sp.name, sp.id, query)) : subProjectOptions;
  }, [subProjectOptions, subProjectSearch]);

  // Reset sub-projects when switching master project.
  useEffect(() => {
    setSubProjectSearch('');
    if (variant === 'userConfig') return;
    if (!selectedMaster) {
      if (subProjectIds.length > 0) {
        form.setValue(fieldName('subProjectIds'), [] as never, { shouldDirty: true });
      }
      return;
    }
    const validIds = new Set(selectedMaster.subProjects?.map((sp) => sp.id) ?? []);
    const filtered = subProjectIds.filter((id) => validIds.has(id));
    if (filtered.length !== subProjectIds.length) {
      form.setValue(fieldName('subProjectIds'), filtered as never, { shouldDirty: true });
    }
    // We intentionally don't include subProjectIds in deps — we only want this
    // to run when the master project changes, otherwise it loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMaster?.id]);

  const handleGenerate = (): void => {
    form.setValue(fieldName('password'), generatePassword() as never, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setShowCopied(false);
  };

  const toggleSubProject = (id: number, checked: boolean): void => {
    const next = checked ? [...subProjectIds, id] : subProjectIds.filter((x) => x !== id);
    form.setValue(fieldName('subProjectIds'), next as never, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const toggleMasterProject = (id: number, checked: boolean): void => {
    const next = checked ? [...projectIds, id] : projectIds.filter((x) => x !== id);
    form.setValue(fieldName('projectIds'), next as never, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const passwordLabel = mode === 'create' ? 'Contraseña' : 'Nueva contraseña (opcional)';
  const passwordPlaceholder = mode === 'edit' ? 'Dejar vacío para no cambiar' : '';

  const renderSubProjectList = (): JSX.Element => {
    if (selectedMaster === null) {
      return (
        <div className="rounded-md border border-dashed border-border-subtle px-3 py-4">
          <p className="text-xs text-text-subtle">Selecciona una inmobiliaria primero.</p>
        </div>
      );
    }
    if (subProjectOptions.length === 0) {
      return (
        <div className="rounded-md border border-dashed border-border-subtle px-3 py-4">
          <p className="text-xs text-text-subtle">Esta inmobiliaria no tiene sub-proyectos.</p>
        </div>
      );
    }
    return (
      <ScrollArea
        viewportClassName="min-h-0 flex-1"
        className="flex min-h-24 max-h-56 flex-1 flex-col rounded-md border border-border-subtle bg-bg-surface"
      >
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border-subtle bg-bg-surface px-2.5 py-1.5">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            value={subProjectSearch}
            onChange={(e) => setSubProjectSearch(e.target.value)}
            placeholder="Buscar sub-proyecto…"
            className="h-6 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        {filteredSubProjectOptions.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-text-subtle">Sin resultados.</p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 p-2">
            {filteredSubProjectOptions.map((sp) => {
              const checked = subProjectIds.includes(sp.id);
              return (
                <li key={sp.id}>
                  <Label
                    htmlFor={`sp-${sp.id}`}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm font-normal hover:bg-bg-surface-alt"
                  >
                    <Checkbox
                      id={`sp-${sp.id}`}
                      checked={checked}
                      onCheckedChange={(c) => toggleSubProject(sp.id, c === true)}
                    />
                    <span className="flex min-w-0 items-baseline gap-1.5">
                      <span className="truncate">{sp.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">#{sp.id}</span>
                    </span>
                  </Label>
                </li>
              );
            })}
          </ul>
        )}
      </ScrollArea>
    );
  };

  const nameField = (
    <FormField
      control={form.control}
      name={fieldName('name')}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Nombre</FormLabel>
          <FormControl>
            <Input
              name={field.name}
              value={(field.value as string) ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const emailField = (
    <FormField
      control={form.control}
      name={fieldName('email')}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Email</FormLabel>
          <FormControl>
            <Input
              type="email"
              name={field.name}
              value={(field.value as string) ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const phoneField = (
    <FormField
      control={form.control}
      name={fieldName('phoneNumber')}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Teléfono</FormLabel>
          <FormControl>
            <Input
              name={field.name}
              value={(field.value as string) ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const passwordField = (
    <FormField
      control={form.control}
      name={fieldName('password')}
      render={({ field }) => {
        const fieldPassword = ((field.value as string) ?? '').trim();
        const canCopyPassword = fieldPassword.length > 0;

        const handleCopy = async (): Promise<void> => {
          if (!fieldPassword) return;
          try {
            await navigator.clipboard.writeText(fieldPassword);
            setShowCopied(true);
            setTimeout(() => setShowCopied(false), COPIED_FEEDBACK_MS);
          } catch {
            // Clipboard might be unavailable (non-HTTPS contexts); user can copy manually.
          }
        };

        return (
          <FormItem>
            <FormLabel>{passwordLabel}</FormLabel>
            <div className="flex items-center gap-2">
              <FormControl>
                <Input
                  type="text"
                  placeholder={passwordPlaceholder}
                  name={field.name}
                  value={(field.value as string) ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                />
              </FormControl>
              <Button type="button" size="sm" variant="outline" onClick={handleGenerate} title="Generar contraseña">
                <RefreshCw />
                <span>Generar</span>
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  void handleCopy();
                }}
                disabled={!canCopyPassword}
                title="Copiar al portapapeles"
              >
                <Copy />
                <span>Copiar</span>
              </Button>
            </div>
            {showCopied && <p className="text-xs text-success">Contraseña copiada al portapapeles</p>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );

  const externalIdField = (
    <FormField
      control={form.control}
      name={fieldName('externalId')}
      render={({ field }) => (
        <FormItem>
          <FormLabel>ID externo</FormLabel>
          <FormControl>
            <Input
              name={field.name}
              value={(field.value as string) ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const projectField = (
    <Controller
      control={form.control}
      name={fieldName('projectId')}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Inmobiliaria</FormLabel>
          <Popover
            open={projectPopoverOpen}
            onOpenChange={(open) => {
              setProjectPopoverOpen(open);
              if (!open) setProjectSearch('');
            }}
          >
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" className="w-full justify-between font-normal">
                <span
                  className={cn('flex min-w-0 items-baseline gap-1.5', !selectedProjectName && 'text-muted-foreground')}
                >
                  <span className="truncate">{selectedProjectName ?? 'Selecciona una inmobiliaria'}</span>
                  {selectedProjectName && projectId !== null && (
                    <span className="shrink-0 text-xs text-muted-foreground">#{projectId}</span>
                  )}
                </span>
                <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-(--radix-popover-trigger-width) gap-0 p-0">
              <ScrollArea className="h-56">
                <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border-subtle bg-bg-surface px-2.5 py-1.5">
                  <Search className="size-4 shrink-0 text-muted-foreground" />
                  <input
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    placeholder="Buscar inmobiliaria…"
                    className="h-6 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
                {filteredMasterProjects.length === 0 ? (
                  <p className="px-3 py-4 text-center text-xs text-text-subtle">Sin resultados.</p>
                ) : (
                  <ul className="flex flex-col gap-0.5 p-1">
                    {filteredMasterProjects.map((p) => {
                      const isSelected = p.id === projectId;
                      return (
                        <li key={p.id}>
                          <button
                            type="button"
                            className={cn(
                              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-bg-surface-alt',
                              isSelected && 'bg-bg-surface-alt font-medium'
                            )}
                            onClick={() => {
                              field.onChange(p.id);
                              setProjectPopoverOpen(false);
                              setProjectSearch('');
                            }}
                          >
                            <Check className={cn('size-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
                            <span className="flex min-w-0 items-baseline gap-1.5">
                              <span className="truncate">{p.name}</span>
                              <span className="shrink-0 text-xs text-muted-foreground">#{p.id}</span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </ScrollArea>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const renderMasterProjectList = (): JSX.Element => {
    if (masterProjects.length === 0) {
      return (
        <div className="rounded-md border border-dashed border-border-subtle px-3 py-4">
          <p className="text-xs text-text-subtle">No hay inmobiliarias disponibles.</p>
        </div>
      );
    }
    return (
      <ScrollArea
        viewportClassName="min-h-0 flex-1"
        className="flex min-h-24 max-h-56 flex-1 flex-col rounded-md border border-border-subtle bg-bg-surface"
      >
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border-subtle bg-bg-surface px-2.5 py-1.5">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            value={masterProjectSearch}
            onChange={(e) => setMasterProjectSearch(e.target.value)}
            placeholder="Buscar inmobiliaria…"
            className="h-6 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        {filteredMasterProjectsForChecklist.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-text-subtle">Sin resultados.</p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 p-2">
            {filteredMasterProjectsForChecklist.map((mp) => {
              const checked = projectIds.includes(mp.id);
              return (
                <li key={mp.id}>
                  <Label
                    htmlFor={`mp-${mp.id}`}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm font-normal hover:bg-bg-surface-alt"
                  >
                    <Checkbox
                      id={`mp-${mp.id}`}
                      checked={checked}
                      onCheckedChange={(c) => toggleMasterProject(mp.id, c === true)}
                    />
                    <span className="flex min-w-0 items-baseline gap-1.5">
                      <span className="truncate">{mp.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">#{mp.id}</span>
                    </span>
                  </Label>
                </li>
              );
            })}
          </ul>
        )}
      </ScrollArea>
    );
  };

  const masterProjectsField = (
    <FormField
      control={form.control}
      name={fieldName('projectIds')}
      render={() => (
        <FormItem className="flex min-h-0 flex-1 flex-col">
          <FormLabel>Inmobiliarias</FormLabel>
          {renderMasterProjectList()}
          {variant === 'userConfig' ? (
            <FormDescription>
              El usuario que se cree en un proyecto particular no tendrá permisos. Hay que asignárselos manualmente.
            </FormDescription>
          ) : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const subProjectsField = (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <Label>Sub-proyectos asignados</Label>
      {renderSubProjectList()}
    </div>
  );

  const assignableField = (
    <Controller
      control={form.control}
      name={fieldName('isAssignable')}
      render={({ field }) => (
        <div className="flex items-center justify-between">
          <Label htmlFor="isAssignable" className="cursor-pointer font-normal">
            Puede ser asignado
          </Label>
          <Switch id="isAssignable" checked={Boolean(field.value)} onCheckedChange={field.onChange} />
        </div>
      )}
    />
  );

  const activeField = (
    <Controller
      control={form.control}
      name={fieldName('isActive')}
      render={({ field }) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="isActive" className="cursor-pointer font-normal">
              Activo
            </Label>
            <Switch
              id="isActive"
              checked={Boolean(field.value)}
              disabled={!canToggleActive}
              onCheckedChange={field.onChange}
            />
          </div>
          {isPendingActivation && (
            <p className="text-xs text-text-subtle">
              Pendiente de activación — no se puede activar/desactivar manualmente.
            </p>
          )}
          {isSelf && !isPendingActivation && (
            <p className="text-xs text-text-subtle">No puedes modificar tu propio estado.</p>
          )}
        </div>
      )}
    />
  );

  const renderFields = (): JSX.Element => {
    if (variant === 'userConfig') {
      return (
        <>
          {nameField}
          {emailField}
          {phoneField}
          {passwordField}
          {isAdminUser ? (
            <p className="text-xs text-text-subtle">Usuario admin: no se asocia a inmobiliarias.</p>
          ) : (
            masterProjectsField
          )}
        </>
      );
    }
    if (variant === 'projectPermissions') {
      return (
        <>
          {activeField}
          {externalIdField}
          {subProjectsField}
          {assignableField}
        </>
      );
    }
    return (
      <>
        {nameField}
        {emailField}
        {phoneField}
        {passwordField}
        {externalIdField}
        {projectField}
        {subProjectsField}
        {assignableField}
        {mode === 'edit' && activeField}
      </>
    );
  };

  const sectionTitle = variant === 'projectPermissions' ? 'Permisos de proyecto' : 'Info usuario';

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-4">
      {variant !== 'userConfig' && (
        <h3 className="text-sm font-semibold uppercase tracking-wide text-text-subtle font-body">{sectionTitle}</h3>
      )}
      {renderFields()}
    </section>
  );
};

export default UserInfoSection;
